/**
 * feat-243 — every user-visible string the conflict banner adds, in one module.
 *
 * SAME SHAPE AND SAME REASONING AS `map/mapCopy.ts`. Nouns naming a Track or a
 * Lesson render through `QUIZ_TERMS`, never a literal: a literal-text grep
 * returns a **true zero** for on-screen copy in this repo, so a literal here is
 * invisible to exactly the instrument that would catch it. Keeping the copy in
 * one place makes "did every noun go through the constant?" a question you can
 * answer by reading one file — and it is where QA will look.
 *
 * The exact wording is `design/CONFLICT-BRIEF.md` §4. Where a case in the tables
 * there does not enumerate a combination the data can produce (two `moved`
 * entries; an `added` paired with a `moved`), the generalisation is marked below
 * with what it extends and why.
 *
 * TWO RULES THE COPY ITSELF CARRIES:
 *
 *  1. **`{who}` is the raw email, verbatim.** Not a derived display name.
 *     `spec.md` §4's illustrative "Jenna" works for exactly one address;
 *     `j.smith@`, `admin@` and `curriculum-development-team@` become "J.smith",
 *     "Admin" and "Curriculum-development-team". The server sends an email
 *     because the audit row holds an email, so the banner and
 *     `curriculum_change_history` say the same thing and the author can act on
 *     it — message that person.
 *
 *  2. **`changed_by: null` is NOT "Someone".** Null means there is no audit row,
 *     which can mean migration 114's backfill wrote the membership and no person
 *     was involved. "Someone added…" would assert a person we cannot evidence,
 *     so the anonymous branch is passive and says why the name is missing.
 */

import { QUIZ_TERMS } from '../../quizTerminology';
import type { ConflictScope } from './trackConflict';

const track = () => QUIZ_TERMS.track.singular;

/** Labels and glyphs. Not routed through `QUIZ_TERMS` — none of them names a
 *  Track or a Lesson. */
export const CONFLICT_LABELS = {
  /** The banner's mark. Two arrows, because the fact is "two writes crossed" —
   *  not a warning triangle, which is `DefaultTrackAlert`'s and means an
   *  engineering fault. */
  bannerGlyph: '⇄',
  reapply: 'Re-apply my change',
  /** AC16 names the label above; the in-flight label is the ONLY variation. */
  reapplying: 'Re-applying…',
  dismiss: 'Dismiss',
  reorderPill: 'Nothing to re-apply for a reorder',
  noopPill: 'Nothing to re-apply',
  /** Chip-row label, `n <= 3`. */
  theyChanged: 'They changed:',
  /** Same row when `changed_by` is null — nobody to call "they". */
  whatChanged: 'What changed:',
} as const;

/**
 * THE DIFF IS CARRIED BY GLYPH, NEVER BY HUE. `attention-surface #FFF4D6` and
 * `danger-surface #FEF2F2` are L 0.908 vs 0.910; a design leaning on fill
 * collapses in greyscale and for a red-deficient reader. Same rule as
 * `mapCopy.ts`'s `HAZARD_GLYPH`.
 *
 * `removed` is U+2212 MINUS SIGN, **not** a hyphen-minus — it is the character
 * that optically balances `+` at this size.
 */
export const DIFF_GLYPH = {
  added: '+',
  removed: '−',
  moved: '⇅',
} as const;

export type DiffKind = keyof typeof DIFF_GLYPH;

/** Max chips before the row collapses into a plain count. */
export const CHIP_CAP = 6;

/** `{list}` — titles joined `A, B and C`, capped at 3 then `…and {k} more`.
 *  Past the cap the natural "and" is dropped: "A, B and C …and 2 more" reads as
 *  two competing lists, so the truncated form is a plain comma series. */
export function joinTitles(titles: string[], cap = 3): string {
  if (titles.length === 0) return '';
  if (titles.length === 1) return titles[0];
  if (titles.length <= cap) {
    return `${titles.slice(0, -1).join(', ')} and ${titles[titles.length - 1]}`;
  }
  return `${titles.slice(0, cap).join(', ')} …and ${titles.length - cap} more`;
}

const PAST_TENSE: Record<DiffKind, string> = {
  added: 'added',
  removed: 'removed',
  moved: 'moved',
};

/** The kinds with entries, in the order they are spoken. */
function presentKinds(groups: Record<DiffKind, string[]>): DiffKind[] {
  return (['added', 'removed', 'moved'] as DiffKind[]).filter((k) => groups[k].length > 0);
}

export const CONFLICT_COPY = {
  /**
   * §4a / §4b — who, and what. `who` is the raw `changed_by`, or null.
   *
   * Titles are already resolved by the caller, so this module never touches
   * `unitsById` and stays a pure string module.
   *
   * **Prereqs never enumerate, even at n === 1.** A prereq change is about
   * gating, not about a list, and "added Coffee Fundamentals" reads as a
   * membership change to someone holding both subtabs in their head. The chips
   * carry the names.
   *
   * **The headline is allowed to degrade at n >= 3** because no detail is lost —
   * it moves into the chip rows. Its job there is to say *someone else was in
   * here and it was substantial*; the chips answer *what*.
   */
  headline(o: {
    who: string | null;
    scope: ConflictScope;
    added: string[];
    removed: string[];
    moved: string[];
  }): string {
    if (o.scope === 'prereqs') {
      return o.who
        ? `${o.who} changed what this ${track()} requires while you were editing.`
        : `What this ${track()} requires changed while you were editing.`;
    }

    const groups: Record<DiffKind, string[]> = {
      added: o.added,
      removed: o.removed,
      moved: o.moved,
    };
    const n = o.added.length + o.removed.length + o.moved.length;
    const kinds = presentKinds(groups);

    if (n >= 3 || kinds.length === 0) {
      return o.who
        ? `${o.who} made ${n} changes while you were editing.`
        : `${n} changes were made while you were editing.`;
    }

    if (kinds.length === 1) {
      const kind = kinds[0];
      const list = joinTitles(groups[kind]);
      if (o.who) return `${o.who} ${PAST_TENSE[kind]} ${list} while you were editing.`;
      // n is 1 or 2 here, and the verb has to agree with the count — a helper
      // that returns a joined list does not make the sentence around it plural.
      return `${list} ${n === 1 ? 'was' : 'were'} ${PAST_TENSE[kind]} while you were editing.`;
    }

    // n === 2 across two kinds. §4b's table names added+removed; added+moved and
    // removed+moved are the same sentence with the other verb, which is the
    // generalisation rather than a new rule.
    const [first, second] = kinds;
    const a = groups[first][0];
    const b = groups[second][0];
    if (o.who) {
      return `${o.who} ${PAST_TENSE[first]} ${a} and ${PAST_TENSE[second]} ${b} while you were editing.`;
    }
    return `${a} was ${PAST_TENSE[first]} and ${b} was ${PAST_TENSE[second]} while you were editing.`;
  },

  /** §4c — the sub-line. `isReorder` is the derived pure-reorder case (S4). */
  subline(o: { scope: ConflictScope; isReorder: boolean }): string {
    if (o.scope === 'prereqs') {
      return "Your change wasn't saved. The requirements below are the current ones.";
    }
    return o.isReorder
      ? "Your reorder wasn't saved. The order below is the current one."
      : "Your change wasn't saved. The order below is the current one.";
  },

  /** §4b — appended to the sub-line, and ONLY in the null case. The gap then
   *  reads as a known limit rather than as a rendering bug. */
  anonymousNote(): string {
    return (
      `We don't have a record of who made the change — this ${track()}'s history ` +
      'starts before we began tracking edits.'
    );
  },

  /** §4d — the `n <= 3` single-row label. */
  chipRowLabel(anonymous: boolean): string {
    return anonymous ? CONFLICT_LABELS.whatChanged : CONFLICT_LABELS.theyChanged;
  },

  /** §4d — the `n >= 4` per-group labels. */
  groupLabel(kind: DiffKind, k: number): string {
    const word = kind === 'added' ? 'Added' : kind === 'removed' ? 'Removed' : 'Moved';
    return `${word} ${k}`;
  },

  /** Overflow past `CHIP_CAP`. A count, not a thing — so it carries no chip
   *  border. */
  moreCount(k: number): string {
    return `+${k} more`;
  },

  /**
   * §4e — the line beside the button, which says what the button will do BEFORE
   * it is pressed. That is what makes an explicit replay honest rather than a
   * merge with a button on it. Do not drop it to save a row.
   */
  reapplyLine(o: { scope: ConflictScope; adds: string[]; removes: string[] }): string {
    const adds = joinTitles(o.adds);
    const removes = joinTitles(o.removes);
    if (o.scope === 'prereqs') {
      if (adds && removes) {
        return `Re-applying adds ${adds} and removes ${removes} from the current requirements.`;
      }
      if (adds) return `Re-applying adds ${adds} to the current requirements.`;
      return `Re-applying removes ${removes} from the current requirements.`;
    }
    if (adds && removes) return `Re-applying adds ${adds} and removes ${removes}.`;
    if (adds) return `Re-applying adds ${adds} to the end of the current order.`;
    return `Re-applying removes ${removes} from the current order.`;
  },

  /**
   * §4f — S4. The absence of a control is invisible; a sentence in the control's
   * slot is a decision. This one states the mechanism, the risk of the
   * alternative, and the next action, so an author who reads it does not go
   * looking for a button.
   */
  reorderReason(): string {
    return (
      'A drag says "put this one above that one", and the list it pointed at no longer ' +
      "exists — replaying it could land things somewhere you didn't choose. Drag again on " +
      'the order below.'
    );
  },

  /**
   * §4f — S7, the one state where the outcome is good news, so the sentence says
   * so. Plural agreement is handled here because the data can carry more than
   * one id even though the brief's example carries one.
   */
  noopReason(o: { scope: ConflictScope; adds: string[]; removes: string[] }): string {
    const where = o.scope === 'prereqs' ? 'the current requirements' : 'the current order';
    const same = 'your change and theirs were the same.';
    const adds = joinTitles(o.adds);
    const removes = joinTitles(o.removes);
    if (adds && !removes) {
      return `${adds} ${o.adds.length === 1 ? 'is' : 'are'} already in ${where} — ${same}`;
    }
    if (removes && !adds) {
      return `${removes} ${o.removes.length === 1 ? 'is' : 'are'} already gone from ${where} — ${same}`;
    }
    if (adds && removes) {
      // Unreachable from today's controls — each fires one request — so this is
      // the honest general sentence rather than a fourth hand-tuned one.
      return `${adds} and ${removes} already match ${where} — ${same}`;
    }
    // No add and no remove: a prereq write whose set never changed. Nothing was
    // lost, and there is nothing to replay. (Written out rather than
    // capitalising `where`, because "The current requirements already matches"
    // is what a clever one-liner produces here.)
    return o.scope === 'prereqs'
      ? `The current requirements already match what you sent — ${same}`
      : `The current order already matches what you sent — ${same}`;
  },
} as const;
